type TriggerType = 'floating' | 'custom' | 'none';
type TriggerPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left' | 'middle-right' | 'middle-left';
type AnnotationTool = 'highlight' | 'arrow' | 'rectangle' | 'blur' | 'text';

import {
  ConsentController,
  isValidRuntimeConsentPolicy,
  sanitizeConsentProviders,
  type ConsentProviderDefinition,
  type PersistedConsentState,
  type RuntimeConsentPolicy,
} from './consent';
import {
  resolveConsentedRuntime,
  resolveRecorderStopMode,
  type RecorderStopReason,
} from './consent-runtime';
import {
  consentLocaleCandidates,
  requestedConsentLocale,
  subscribeToPageLanguage,
  watchConsentLocale,
} from './consent-locale';
import { resolveConsentUiLocale } from './consent-i18n';
import { trackProperties } from './track-properties';
import { normalizeUrlPolicy, type UrlPolicy } from './url-policy';

interface CustomField {
  type: 'text' | 'select' | 'checkbox' | 'textarea';
  name: string;
  label: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
}

interface FeedbackResult {
  id: string;
  status: string;
  message: string;
}

interface BrowserWidgetConfig {
  trigger?: {
    type: TriggerType;
    position?: TriggerPosition;
    label?: string;
    icon?: string;
    keyboardShortcut?: string;
  };
  theme?: {
    mode?: 'light' | 'dark' | 'system';
    primaryColor?: string;
    textColor?: string;
    backgroundColor?: string;
    borderRadius?: number;
    fontFamily?: string;
  };
  form?: {
    types?: Array<{
      value: string;
      label: string;
      icon?: string;
    }>;
    priorities?: Array<{
      value: string;
      label: string;
      color?: string;
    }>;
    showType?: boolean;
    showTitle?: boolean;
    showPriority?: boolean;
    showEmail?: boolean;
    emailRequired?: boolean;
    customFields?: CustomField[];
  };
  screenshot?: {
    enabled?: boolean;
    required?: boolean;
    tools?: AnnotationTool[];
  };
  metadata?: {
    includeConsole?: boolean;
    includeNetwork?: boolean;
    includeSessionRecording?: boolean;
    customData?: () => Record<string, unknown> | Promise<Record<string, unknown>>;
  };
  onOpen?: () => void;
  onClose?: () => void;
  onSubmit?: (feedback: FeedbackResult) => void;
  onError?: (error: Error) => void;
  user?: {
    id?: string;
    email?: string;
    name?: string;
    [key: string]: unknown;
  };
}

interface BrowserRecorderConfig {
  enabled?: boolean;
  bufferDuration?: number;
  uploadInterval?: number;
  maskingMode?: 'strict' | 'balanced' | 'relaxed';
  maskAllInputs?: boolean;
  maskSelectors?: string[];
  ignoreSelectors?: string[];
  unmaskSelectors?: string[];
  maskNetworkBodies?: boolean;
  scanTextForPII?: boolean;
  captureConsole?: boolean;
  captureNetwork?: boolean;
  captureMouseMove?: boolean;
  mouseMoveThrottle?: number;
  scrollThrottle?: number;
  propagateTraceparent?: boolean;
  tracePropagationOrigins?: string[];
}

interface BrowserConsentConfig {
  enabled?: boolean;
  projectId?: string;
  manageButtonPosition?: TriggerPosition;
  manageButtonVisible?: boolean;
  bannerVisible?: boolean;
  autoConsent?: boolean;
}

export interface PerspectifyBrowserConfig extends BrowserWidgetConfig {
  /** Required for the recorder and feedback widget; unused in consent-only mode. */
  apiKey?: string;
  urlPolicy?: UrlPolicy;
  /** Own only consent UI and decisions, without loading recorder or feedback assets. */
  consentOnly?: boolean;
  apiEndpoint?: string;
  assetsBaseUrl?: string;
  releaseManifestUrl?: string;
  bootstrapOrigin?: string;
  consentStyleNonce?: string;
  recorder?: BrowserRecorderConfig;
  consent?: BrowserConsentConfig;
}

export interface PerspectifyBrowserAPI {
  init: (config: PerspectifyBrowserConfig) => Promise<PerspectifyBrowserAPI>;
  open: () => void;
  close: () => void;
  captureSession: () => Promise<string | null>;
  getSessionId: () => string | null;
  identify: (userId: string, options?: { internal?: boolean }) => Promise<void>;
  resetIdentity: () => Promise<void>;
  track: (name: string, properties?: Record<string, unknown>) => Promise<boolean>;
  destroy: () => Promise<void>;
}

interface RecorderInstance {
  getUrlPolicy?: () => UrlPolicy;
  isCaptureIgnored?: (node: Node) => boolean;
  start: () => void;
  // An older recorder bundle ignores the argument and still flushes. The SDK
  // cannot force a newer bundle on a host page, so treat the discard as
  // best-effort here and keep the guarantee in the recorder it ships with.
  stop?: (options?: { discardBuffer?: boolean }) => Promise<void>;
  capture?: () => Promise<string>;
  getSessionId?: () => string;
  isActive?: () => boolean;
  getUserId?: () => string | undefined;
  setUserId?: (userId: string, internal?: boolean) => void;
  identify?: (userId: string, internal?: boolean) => Promise<void>;
  track?: (name: string, properties?: Record<string, unknown>) => boolean;
  forgetSession?: () => void;
}

type RecorderConstructor = (new (config: Record<string, unknown>) => RecorderInstance) & { urlPolicyVersion?: number };

interface WidgetRuntime {
  urlPolicyVersion?: number;
  getUrlPolicy?: () => UrlPolicy | undefined;
  init: (config: Record<string, unknown>) => unknown;
  open?: () => void;
  close?: () => void;
  destroy?: () => void;
}

type ReleaseAssetKey = 'browser' | 'recorder' | 'widgetScript' | 'widgetStyle';

interface ReleaseAsset {
  path: string;
  url: string;
  integrity: string;
  sha256: string;
  bytes: number;
  contentType: string;
}

interface EmbedReleaseManifest {
  schemaVersion: 1;
  releaseId: string;
  assetOrigin: string;
  bootstrapOrigin: string;
  manifestPath: string;
  assets: Record<ReleaseAssetKey, ReleaseAsset>;
}

interface CompiledReleaseAssets {
  bootstrapOrigin: string;
  assets: {
    recorder: Pick<ReleaseAsset, 'integrity'>;
    widgetScript: Pick<ReleaseAsset, 'integrity'>;
    widgetStyle: Pick<ReleaseAsset, 'integrity'>;
  };
}

interface RuntimeAssetReference {
  url: string;
  integrity?: string;
}

interface RuntimeReleaseAssets {
  key: string;
  bootstrapOrigin?: string;
  assets: {
    recorder: RuntimeAssetReference;
    widgetScript: RuntimeAssetReference;
    widgetStyle: RuntimeAssetReference;
  };
}

const COMPILED_RELEASE_ASSETS: CompiledReleaseAssets | null = JSON.parse('null');

declare global {
  interface Window {
    PerspectifyBrowser?: PerspectifyBrowserAPI;
    __perspectifyBrowserOwnsRecorder?: boolean;
    PerspectifyConsent?: {
      getState: () => PersistedConsentState | null;
      openPreferences: () => void;
      onChange: (listener: (state: PersistedConsentState | null) => void) => () => void;
      canUse: (key: string) => boolean;
      getProviders: () => ConsentProviderDefinition[];
    };
    PerspectifyRecorder?: RecorderConstructor | { PerspectifyRecorder: RecorderConstructor };
    PerspectifyWidget?: WidgetRuntime;
    perspectifyRecorder?: RecorderInstance;
  }
}

let sdkInstance: PerspectifyBrowserAPI | null = null;
let managedRecorder: RecorderInstance | null = null;
const retiredRecorders = new Set<RecorderInstance>();
const privacyCapableRecorders = new WeakSet<RecorderInstance>();
let managedWidgetRuntime: WidgetRuntime | null = null;
let initRequestGeneration = 0;
let detectedAssetsBaseUrl = detectAssetsBaseUrl();
let assetLoadPromise: Promise<void> | null = null;
let loadedRuntimeComplete = false;
let loadedAssetsBaseUrl: string | null = null;
let loadedRuntimeReleaseKey: string | null = null;
let releaseManifestPromise: Promise<EmbedReleaseManifest | null> | null = null;
let releaseManifestCacheKey: string | null = null;
let runtimeSignature: string | null = null;
let activeConsentController: ConsentController | null = null;
let stopConsentLocaleWatch: (() => void) | null = null;
let activeConfig: PerspectifyBrowserConfig | null = null;
// No identifier is inferred from widget profile fields or persisted before
// consent. This value only comes from the application's explicit identify call.
// null is an explicit reset that must also be applied before initialization.
let pendingUserId: string | null | undefined;
// Set only when the application marked the person as its own team; travels with the identity.
let pendingInternal: boolean | undefined;
let runtimeGeneration = 0;

function detectAssetsBaseUrl(): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (currentScript?.src?.includes('perspectify-browser')) {
    return new URL('.', currentScript.src).toString();
  }

  const script = document.querySelector(
    'script[src*="perspectify-browser"]'
  ) as HTMLScriptElement | null;

  return script?.src ? new URL('.', script.src).toString() : null;
}

function normalizeBaseUrl(value: string): string {
  return value.endsWith('/') ? value : `${value}/`;
}

function resolveAssetsBaseUrl(config?: PerspectifyBrowserConfig): string {
  if (config?.assetsBaseUrl) {
    return normalizeBaseUrl(config.assetsBaseUrl);
  }

  if (detectedAssetsBaseUrl) {
    return normalizeBaseUrl(detectedAssetsBaseUrl);
  }

  throw new Error(
    '[Perspectify Browser] assetsBaseUrl is required when the SDK is not loaded from the hosted browser bundle.'
  );
}

function resolveAssetUrl(baseUrl: string, relativePath: string): string {
  return new URL(relativePath, baseUrl).toString();
}

function normalizeOptionalUrl(value: string | undefined, field: string): string | undefined {
  if (value === undefined || value.trim() === '') {
    return undefined;
  }

  try {
    return new URL(value).toString();
  } catch {
    throw new Error(`[Perspectify Browser] ${field} must be an absolute URL.`);
  }
}

function normalizeOriginUrl(value: string | undefined, field: string): string | undefined {
  const normalized = normalizeOptionalUrl(value, field);
  return normalized ? new URL(normalized).origin : undefined;
}

function isReleaseAsset(value: unknown): value is ReleaseAsset {
  if (!value || typeof value !== 'object') return false;
  const asset = value as Record<string, unknown>;
  return (
    typeof asset.path === 'string' &&
    typeof asset.url === 'string' &&
    typeof asset.integrity === 'string' &&
    asset.integrity.startsWith('sha384-') &&
    typeof asset.sha256 === 'string' &&
    typeof asset.bytes === 'number' &&
    Number.isFinite(asset.bytes) &&
    asset.bytes > 0 &&
    typeof asset.contentType === 'string'
  );
}

function parseReleaseManifest(value: unknown): EmbedReleaseManifest | null {
  if (!value || typeof value !== 'object') return null;
  const manifest = value as Record<string, unknown>;
  const assets = manifest.assets as Record<ReleaseAssetKey, unknown> | undefined;
  if (!assets) return null;
  if (
    manifest.schemaVersion !== 1 ||
    typeof manifest.releaseId !== 'string' ||
    !/^r-[a-f0-9]{24}$/.test(manifest.releaseId) ||
    typeof manifest.assetOrigin !== 'string' ||
    typeof manifest.bootstrapOrigin !== 'string' ||
    typeof manifest.manifestPath !== 'string' ||
    !isReleaseAsset(assets.browser) ||
    !isReleaseAsset(assets.recorder) ||
    !isReleaseAsset(assets.widgetScript) ||
    !isReleaseAsset(assets.widgetStyle)
  ) {
    return null;
  }

  return manifest as unknown as EmbedReleaseManifest;
}

function resolveReleaseManifestUrl(config: PerspectifyBrowserConfig, baseUrl: string): string | null {
  const explicit = normalizeOptionalUrl(config.releaseManifestUrl, 'releaseManifestUrl');
  if (explicit) {
    return explicit;
  }

  const parsed = new URL(baseUrl);
  if (!parsed.pathname.includes('/assets/releases/')) {
    return null;
  }

  return new URL('../perspectify-release-manifest.json', baseUrl).toString();
}

async function resolveReleaseManifest(
  config: PerspectifyBrowserConfig
): Promise<EmbedReleaseManifest | null> {
  const baseUrl = resolveAssetsBaseUrl(config);
  const manifestUrl = resolveReleaseManifestUrl(config, baseUrl);
  if (!manifestUrl) {
    return null;
  }

  const cacheKey = `${baseUrl}|${manifestUrl}|${config.urlPolicy ?? 'full'}`;
  if (releaseManifestPromise && releaseManifestCacheKey === cacheKey) {
    return releaseManifestPromise;
  }

  releaseManifestCacheKey = cacheKey;
  releaseManifestPromise = (async () => {
    const response = await fetch(manifestUrl, { credentials: 'omit',
      ...(config.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}),
    });
    if (!response.ok) {
      throw new Error(
        `[Perspectify Browser] Failed to load release manifest (${response.status}).`
      );
    }

    const manifest = parseReleaseManifest(await response.json());
    if (!manifest) {
      throw new Error('[Perspectify Browser] Release manifest is invalid.');
    }

    return manifest;
  })();

  return releaseManifestPromise;
}

function resolveBootstrapOrigin(config: PerspectifyBrowserConfig, releaseBootstrapOrigin?: string): string {
  return (
    normalizeOriginUrl(config.bootstrapOrigin, 'bootstrapOrigin') ??
    normalizeOriginUrl(releaseBootstrapOrigin, 'releaseBootstrapOrigin') ??
    new URL(resolveAssetsBaseUrl(config)).origin
  );
}

function resolveCompiledReleaseAssets(baseUrl: string): RuntimeReleaseAssets | null {
  if (!COMPILED_RELEASE_ASSETS) {
    return null;
  }

  const parsed = new URL(baseUrl);
  if (!parsed.pathname.includes('/assets/releases/')) {
    return null;
  }

  const { recorder, widgetScript, widgetStyle } = COMPILED_RELEASE_ASSETS.assets;
  return {
    key: [
      recorder.integrity,
      widgetScript.integrity,
      widgetStyle.integrity,
    ].join('|'),
    bootstrapOrigin: COMPILED_RELEASE_ASSETS.bootstrapOrigin,
    assets: {
      recorder: {
        url: resolveAssetUrl(baseUrl, '../recorder/perspectify-recorder.js'),
        integrity: recorder.integrity,
      },
      widgetScript: {
        url: resolveAssetUrl(baseUrl, '../widget/perspectify-widget.js'),
        integrity: widgetScript.integrity,
      },
      widgetStyle: {
        url: resolveAssetUrl(baseUrl, '../widget/perspectify-widget.css'),
        integrity: widgetStyle.integrity,
      },
    },
  };
}

function parseBooleanDatasetValue(value: string | undefined): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }
  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return undefined;
}

function parseCsvDatasetValue(value: string | undefined): string[] | undefined {
  if (!value) {
    return undefined;
  }

  const entries = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);

  return entries.length > 0 ? entries : undefined;
}

function loadExternalAsset(
  tag: 'script' | 'link',
  url: string,
  options: { integrity?: string; referrerPolicy?: 'origin' } = {}
): Promise<void> {
  if (typeof document === 'undefined') {
    return Promise.resolve();
  }

  const existing = document.querySelector(`[data-perspectify-src="${url}"]`);
  if (existing) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const element = document.createElement(tag);
    element.setAttribute('data-perspectify-src', url);
    element.setAttribute('data-perspectify-managed', 'true');
    if (options.referrerPolicy) element.setAttribute('referrerpolicy', options.referrerPolicy);

    if (tag === 'script') {
      const script = element as HTMLScriptElement;
      script.src = url;
      script.async = true;
      if (options.integrity) {
        script.integrity = options.integrity;
        script.crossOrigin = 'anonymous';
      }
      element.addEventListener('load', () => resolve());
      element.addEventListener('error', () =>
        reject(new Error(`[Perspectify Browser] Failed to load ${url}`))
      );
      document.body.appendChild(element);
      return;
    }

    const link = element as HTMLLinkElement;
    link.rel = 'stylesheet';
    link.href = url;
    if (options.integrity) {
      link.integrity = options.integrity;
      link.crossOrigin = 'anonymous';
    }
    element.addEventListener('load', () => resolve());
    element.addEventListener('error', () =>
      reject(new Error(`[Perspectify Browser] Failed to load ${url}`))
    );
    document.head.appendChild(element);
  });
}

async function ensureRuntimeLoaded(config: PerspectifyBrowserConfig): Promise<void> {
  const baseUrl = resolveAssetsBaseUrl(config);
  const compiledReleaseAssets = resolveCompiledReleaseAssets(baseUrl);
  const releaseManifest = compiledReleaseAssets ? null : await resolveReleaseManifest(config);
  if (activeConfig !== config) return;
  const runtimeReleaseKey =
    compiledReleaseAssets?.key ?? releaseManifest?.releaseId ?? 'legacy';
  if (
    assetLoadPromise &&
    loadedAssetsBaseUrl === baseUrl &&
    loadedRuntimeReleaseKey === runtimeReleaseKey
  ) {
    await assetLoadPromise;
    if (activeConfig !== config || loadedRuntimeComplete) return;
    assetLoadPromise = null;
    return ensureRuntimeLoaded(config);
  }

  const previousAssetLoad = assetLoadPromise;
  loadedRuntimeComplete = false;
  loadedAssetsBaseUrl = baseUrl;
  loadedRuntimeReleaseKey = runtimeReleaseKey;
  assetLoadPromise = (async () => {
    await previousAssetLoad?.catch(() => {});
    if (activeConfig !== config) return;
    const widgetStyle = compiledReleaseAssets?.assets.widgetStyle ?? releaseManifest?.assets.widgetStyle;
    const recorder = compiledReleaseAssets?.assets.recorder ?? releaseManifest?.assets.recorder;
    const widgetScript = compiledReleaseAssets?.assets.widgetScript ?? releaseManifest?.assets.widgetScript;

    await loadExternalAsset(
      'link',
      widgetStyle?.url ?? resolveAssetUrl(baseUrl, '../widget/perspectify-widget.css'),
      { integrity: widgetStyle?.integrity, ...(config.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}) }
    );
    if (activeConfig !== config) return;
    await loadExternalAsset(
      'script',
      recorder?.url ?? resolveAssetUrl(baseUrl, '../recorder/perspectify-recorder.js'),
      { integrity: recorder?.integrity, ...(config.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}) }
    );
    if (activeConfig !== config) return;
    await loadExternalAsset(
      'script',
      widgetScript?.url ?? resolveAssetUrl(baseUrl, '../widget/perspectify-widget.js'),
      { integrity: widgetScript?.integrity, ...(config.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}) }
    );

    if (activeConfig !== config) return;
    if (!window.PerspectifyWidget) {
      throw new Error('[Perspectify Browser] Widget runtime failed to initialize.');
    }

    if (!window.PerspectifyRecorder) {
      throw new Error('[Perspectify Browser] Recorder runtime failed to initialize.');
    }
    loadedRuntimeComplete = true;
  })();

  return assetLoadPromise;
}

function getRecorderConstructor(): RecorderConstructor {
  const recorderGlobal = window.PerspectifyRecorder;
  const ctor =
    typeof recorderGlobal === 'function'
      ? recorderGlobal
      : recorderGlobal?.PerspectifyRecorder;

  if (!ctor) {
    throw new Error('[Perspectify Browser] Recorder runtime is unavailable.');
  }

  return ctor;
}

function getWidgetRuntime(): WidgetRuntime {
  if (!window.PerspectifyWidget?.init) {
    throw new Error('[Perspectify Browser] Widget runtime is unavailable.');
  }

  return window.PerspectifyWidget;
}

function buildRecorderConfig(config: PerspectifyBrowserConfig): Record<string, unknown> {
  const recorder = config.recorder ?? {};
  return {
    apiKey: config.apiKey,
    apiEndpoint: config.apiEndpoint,
    urlPolicy: config.urlPolicy,
    bufferDuration: recorder.bufferDuration,
    uploadInterval: recorder.uploadInterval,
    maskingMode: recorder.maskingMode,
    maskAllInputs: recorder.maskAllInputs,
    maskSelectors: recorder.maskSelectors,
    ignoreSelectors: recorder.ignoreSelectors,
    unmaskSelectors: recorder.unmaskSelectors,
    maskNetworkBodies: recorder.maskNetworkBodies,
    scanTextForPII: recorder.scanTextForPII,
    captureConsole: recorder.captureConsole ?? true,
    captureNetwork: recorder.captureNetwork ?? true,
    captureMouseMove: recorder.captureMouseMove,
    mouseMoveThrottle: recorder.mouseMoveThrottle,
    scrollThrottle: recorder.scrollThrottle,
    propagateTraceparent: recorder.propagateTraceparent ?? true,
    tracePropagationOrigins: recorder.tracePropagationOrigins,
  };
}

function buildWidgetConfig(config: PerspectifyBrowserConfig): Record<string, unknown> {
  const recorderEnabled = config.recorder?.enabled !== false;

  return {
    apiKey: config.apiKey,
    apiEndpoint: config.apiEndpoint,
    urlPolicy: config.urlPolicy,
    trigger:
      config.trigger ??
      ({
        type: 'floating',
        position: 'bottom-right',
        label: 'Feedback',
        keyboardShortcut: 'ctrl+shift+f',
      } satisfies NonNullable<PerspectifyBrowserConfig['trigger']>),
    theme: config.theme,
    form: config.form,
    screenshot: {
      enabled: config.screenshot?.enabled ?? true,
      required: config.screenshot?.required,
      tools: config.screenshot?.tools ?? ['highlight', 'arrow', 'rectangle', 'text', 'blur'],
    },
    metadata: {
      includeConsole: config.metadata?.includeConsole ?? true,
      includeNetwork: config.metadata?.includeNetwork ?? true,
      includeSessionRecording: config.metadata?.includeSessionRecording ?? recorderEnabled,
      customData: config.metadata?.customData,
    },
    onOpen: config.onOpen,
    onClose: config.onClose,
    onSubmit: config.onSubmit,
    onError: config.onError,
    user: config.user,
  };
}

function buildRuntimeSignature(config: PerspectifyBrowserConfig): string {
  return JSON.stringify({
    apiKey: config.apiKey,
    apiEndpoint: config.apiEndpoint ?? null,
    urlPolicy: config.urlPolicy,
    assetsBaseUrl: config.assetsBaseUrl ?? null,
    releaseManifestUrl: config.releaseManifestUrl ?? null,
    bootstrapOrigin: config.bootstrapOrigin ?? null,
    recorderEnabled: config.recorder?.enabled !== false,
    recorderConfig: buildRecorderConfig(config),
  });
}

function startManagedRecorder(config: PerspectifyBrowserConfig, sendIdentity = true): void {
  if (config.consentOnly === true || config.recorder?.enabled === false) {
    return;
  }

  if (window.perspectifyRecorder?.isActive?.()) {
    if (config.urlPolicy === 'origin-only' &&
        (window.perspectifyRecorder !== managedRecorder || managedRecorder?.getUrlPolicy?.() !== 'origin-only')) {
      throw new Error('[Perspectify Browser] An unmanaged recorder cannot be adopted for origin-only capture.');
    }
    return;
  }

  const RecorderCtor = getRecorderConstructor();
  if (config.urlPolicy === 'origin-only' && RecorderCtor.urlPolicyVersion !== 1) {
    throw new Error('[Perspectify Browser] Update the recorder bundle for origin-only capture.');
  }
  const createRecorder = () => {
    const instance = new RecorderCtor(buildRecorderConfig(config));
    if (RecorderCtor.urlPolicyVersion === 1 && typeof instance.getUrlPolicy === 'function' && typeof instance.stop === 'function') {
      privacyCapableRecorders.add(instance);
    }
    return instance;
  };
  let recorder = createRecorder();
  // Reconcile restored identity before start() takes its initial snapshot. A
  // new page may already show a different account before its SDK initializes.
  if (pendingUserId !== undefined) {
    const previousUserId = recorder.getUserId?.();
    if (pendingUserId === null || (previousUserId !== undefined && previousUserId !== pendingUserId)) {
      if (!recorder.forgetSession) throw new Error('[Perspectify Browser] Update the recorder bundle to reset identity.');
      recorder.forgetSession();
      recorder = createRecorder();
    }
    if (pendingUserId !== null) {
      if (!recorder.setUserId) throw new Error('[Perspectify Browser] Update the recorder bundle to use identify.');
      recorder.setUserId(pendingUserId, pendingInternal);
    } else {
      pendingUserId = undefined;
      pendingInternal = undefined;
    }
  }
  if (config.urlPolicy === 'origin-only' && recorder.getUrlPolicy?.() !== 'origin-only') {
    throw new Error('[Perspectify Browser] Recorder URL policy was not applied.');
  }
  recorder.start();
  managedRecorder = recorder;
  window.perspectifyRecorder = recorder;
  if (sendIdentity && typeof pendingUserId === 'string') {
    // Failed delivery remains on event payloads and unload beacons. A queued
    // identity must not turn an otherwise successful consent grant into an
    // unhandled rejection in the host application.
    void recorder.identify?.(pendingUserId, pendingInternal).catch(() => {});
  }
}

async function stopManagedRecorder(
  forgetIdentity = false,
  reason: RecorderStopReason = 'teardown'
): Promise<void> {
  const recorder = managedRecorder;
  if (!recorder?.stop) {
    if (window.perspectifyRecorder === recorder) {
      delete window.perspectifyRecorder;
    }
    managedRecorder = null;
    return;
  }

  // Detach synchronously so identity calls cannot adopt a recorder being stopped.
  managedRecorder = null;
  if (window.perspectifyRecorder === recorder) delete window.perspectifyRecorder;
  // Retire storage before awaiting network delivery: a new consent grant or
  // init may start another recorder while the old upload is still pending.
  if (forgetIdentity) recorder.forgetSession?.();
  retiredRecorders.add(recorder);
  try {
    await recorder.stop(resolveRecorderStopMode(reason));
  } catch (error) {
    console.warn('[Perspectify Browser] Recorder shutdown upload failed.', error);
  } finally {
    retiredRecorders.delete(recorder);
  }
}

function canRecord(): boolean {
  return activeConfig !== null && activeConfig.consentOnly !== true && activeConfig.recorder?.enabled !== false &&
    (!activeConsentController || activeConsentController.canUseService('perspectify_recorder'));
}

function applyIdentity(userId: string | null, internal?: boolean): Promise<void> {
  if (!canRecord() || !managedRecorder?.isActive?.() || !activeConfig) return Promise.resolve();
  const previousUserId = managedRecorder.getUserId?.();
  // A reset that drops nothing must not cost a visit: an anonymous recording has no identity
  // to leave behind, so it keeps running. Found in KURT on 2026-09-10, where a widget reset
  // the identity on every mount for people without a pseudonymous id — the usage page counted
  // one page load as two visits. Signing out still rotates (there previousUserId is set).
  if (userId === null && previousUserId === undefined) return Promise.resolve();
  if (userId === null || (previousUserId !== undefined && previousUserId !== userId)) {
    // stop() detaches capture synchronously. Network delivery of the retired
    // session must never delay logout or keep recording a different account.
    void stopManagedRecorder(true, 'identity-rotation');
    startManagedRecorder(activeConfig, false);
  }
  if (userId === null) return Promise.resolve();
  if (!managedRecorder?.identify) {
    return Promise.reject(new Error('[Perspectify Browser] Update the recorder bundle to use identify.'));
  }
  return managedRecorder.identify(userId, internal);
}

/**
 * Associate a pseudonymous application ID with the current recording.
 *
 * `options.internal` marks the person as the application's own team (staff
 * testing on production, for example); the Usage tab leaves such sessions out
 * by default. It travels with the identity and is never inferred.
 */
function identify(userId: string, options?: { internal?: boolean }): Promise<void> {
  if (typeof userId !== 'string' || [...userId].length === 0 || [...userId].length > 256 || /[\u0000-\u001f\u007f\ud800-\udfff]/u.test(userId)) {
    return Promise.reject(new Error('[Perspectify Browser] userId must contain 1–256 Unicode characters without ASCII control characters.'));
  }
  if (options?.internal !== undefined && typeof options.internal !== 'boolean') {
    return Promise.reject(new Error('[Perspectify Browser] internal must be a boolean when provided.'));
  }
  pendingUserId = userId;
  pendingInternal = options?.internal === true ? true : undefined;
  return applyIdentity(userId, pendingInternal);
}

/** `ideation.filter_applied`, `psur.created` — lowercase, dot-namespaced, static. */
const TRACK_NAME_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)*$/;
const TRACK_NAME_MAX_LENGTH = 64;

/**
 * Record what the person did. The event rides the recording session — it is
 * uploaded with the next batch, owned by the identified person (if any) and the
 * team marker — so without an active recording there is nothing to ride and
 * the call resolves false rather than queueing. Resolves true once buffered.
 */
function track(name: string, properties?: Record<string, unknown>): Promise<boolean> {
  if (typeof name !== 'string' || name.length === 0 || name.length > TRACK_NAME_MAX_LENGTH || !TRACK_NAME_PATTERN.test(name)) {
    return Promise.reject(new Error('[Perspectify Browser] track() name must be lowercase, dot-namespaced (e.g. "ideation.filter_applied") and at most 64 characters.'));
  }
  if (properties !== undefined && (properties === null || typeof properties !== 'object' || Array.isArray(properties))) {
    return Promise.reject(new Error('[Perspectify Browser] track() properties must be a plain object.'));
  }
  if (!canRecord() || !managedRecorder?.isActive?.() || !managedRecorder.track) {
    return Promise.resolve(false);
  }
  return Promise.resolve(managedRecorder.track(name, trackProperties(properties)));
}

/** Start a fresh anonymous session after the application signs a user out. */
function resetIdentity(): Promise<void> {
  pendingUserId = null;
  pendingInternal = undefined;
  return applyIdentity(null);
}

async function fetchConsentPolicy(
  config: PerspectifyBrowserConfig,
  releaseBootstrapOrigin?: string,
  signal?: AbortSignal
): Promise<{
  policy: RuntimeConsentPolicy | null;
  apiBaseUrl: string | null;
  bootstrapOrigin: string;
}> {
  const bootstrapOrigin = resolveBootstrapOrigin(config, releaseBootstrapOrigin);
  if (config.consent?.enabled === false || typeof window === 'undefined') {
    return { policy: null, apiBaseUrl: null, bootstrapOrigin };
  }

  const url = new URL('/api/public/consent/site-config', bootstrapOrigin);
  url.searchParams.set('siteOrigin', window.location.origin);
  url.searchParams.set('locale', requestedConsentLocale());
  if (config.consent?.projectId) {
    url.searchParams.set('projectId', config.consent.projectId);
  }

  const response = await fetch(url.toString(), {
    credentials: 'omit',
    signal,
    ...(config.urlPolicy === 'origin-only' ? { referrerPolicy: 'origin' as const } : {}),
  });

  if (!response.ok) {
    throw new Error(`[Perspectify Browser] Failed to load consent policy (${response.status}).`);
  }

  const payload = (await response.json()) as
    | {
        success?: boolean;
        enabled?: boolean;
        apiBaseUrl?: string | null;
        policy?: RuntimeConsentPolicy | null;
      }
    | null;

  if (!payload?.success) {
    return { policy: null, apiBaseUrl: null, bootstrapOrigin };
  }

  const resolvedApiBaseUrl = typeof payload.apiBaseUrl === 'string' ? payload.apiBaseUrl : null;

  if (payload.enabled !== true || !payload.policy) {
    return { policy: null, apiBaseUrl: resolvedApiBaseUrl, bootstrapOrigin };
  }

  return {
    apiBaseUrl: resolvedApiBaseUrl,
    bootstrapOrigin,
    policy: {
      ...payload.policy,
      providers: sanitizeConsentProviders(payload.policy.providers),
      manageButtonPosition:
        config.consent?.manageButtonPosition ?? payload.policy.manageButtonPosition,
    },
  };
}

/**
 * Where the feedback widget draws its floating trigger, mirroring the widget's own defaults:
 * `buildWidgetConfig` falls back to bottom-right, and a trigger without a position renders at
 * middle-right. Null when the widget draws no floating trigger.
 */
function floatingTriggerPosition(config: PerspectifyBrowserConfig): TriggerPosition | null {
  if (config.consentOnly === true) return null;
  if (!config.trigger) return 'bottom-right';
  if (config.trigger.type !== 'floating') return null;
  return config.trigger.position ?? 'middle-right';
}

function attachConsentBridge(controller: ConsentController | null): void {
  if (!controller) {
    delete window.PerspectifyConsent;
    return;
  }

  window.PerspectifyConsent = {
    getState: () => controller.getState(),
    openPreferences: () => controller.openPreferences(),
    onChange: (listener) => controller.onChange(listener),
    canUse: (key: string) => {
      if (
        key === 'perspectify_recorder' ||
        key === 'perspectify_feedback_widget'
      ) {
        return controller.canUseService(key);
      }

      if (
        key === 'necessary' ||
        key === 'functional' ||
        key === 'analytics' ||
        key === 'marketing' ||
        key === 'support'
      ) {
        return controller.canUseCategory(key);
      }

      return false;
    },
    getProviders: () => controller.getProviders(),
  };
  // Lets a host page wire its own "Privacy choices" control without polling for the bridge.
  window.dispatchEvent(new CustomEvent('perspectify:consent-ready'));
}

/**
 * A full navigation already fetches the page's language. This covers a page that switches
 * language in place: only the display copy is refreshed, for the same policy version, and
 * nothing is stored or reported. A failed refresh keeps the copy already shown.
 */
function startConsentLocaleWatch(
  config: PerspectifyBrowserConfig,
  controller: ConsentController,
  releaseBootstrapOrigin: string | undefined,
  initialCandidates: string[]
): void {
  stopConsentLocaleWatch?.();
  stopConsentLocaleWatch = watchConsentLocale({
    initialLocale: resolveConsentUiLocale(initialCandidates),
    readCandidates: consentLocaleCandidates,
    fetchPolicy: async (signal) =>
      (await fetchConsentPolicy(config, releaseBootstrapOrigin, signal)).policy,
    apply: (policy, candidates) => {
      if (activeConfig !== config || activeConsentController !== controller) return;
      controller.updateLocalizedCopy(policy, candidates);
    },
    subscribe: subscribeToPageLanguage,
  });
}

async function applyConsentControlledRuntime(
  config: PerspectifyBrowserConfig,
  controller: ConsentController | null
): Promise<void> {
  if (activeConfig !== config || activeConsentController !== controller) return;
  if (config.consentOnly === true) return;
  const { recorder: allowRecorder, widget: allowWidget } = resolveConsentedRuntime(
    config,
    controller
  );

  if (!allowWidget) {
    window.PerspectifyWidget?.destroy?.();
    managedWidgetRuntime = null;
  }
  if (!allowRecorder && allowWidget) {
    const hadIdentity = managedRecorder?.getUserId?.() !== undefined;
    if (hadIdentity) { pendingUserId = undefined; pendingInternal = undefined; runtimeGeneration++; }
    cancelRetiredRecorders();
    void stopManagedRecorder(hadIdentity, 'consent-withdrawn');
  }

  if (!allowRecorder && !allowWidget) {
    window.PerspectifyWidget?.destroy?.();
    const hadIdentity = managedRecorder?.getUserId?.() !== undefined;
    if (hadIdentity) {
      pendingUserId = undefined;
      pendingInternal = undefined;
      runtimeGeneration++;
    }
    cancelRetiredRecorders();
    await stopManagedRecorder(hadIdentity, 'consent-withdrawn');
    return;
  }

  await ensureRuntimeLoaded(config);
  if (activeConfig !== config || activeConsentController !== controller) return;

  if (config.urlPolicy === 'origin-only') {
    assertManagedPrivacyCompatibility();
    const allowed = resolveConsentedRuntime(config, controller);
    if (allowed.recorder && getRecorderConstructor().urlPolicyVersion !== 1) {
      throw new Error('[Perspectify Browser] Update the recorder bundle for origin-only capture.');
    }
    if (allowed.widget && (getWidgetRuntime().urlPolicyVersion !== 1 || !getWidgetRuntime().getUrlPolicy)) {
      throw new Error('[Perspectify Browser] Update the widget bundle for origin-only capture.');
    }
  }

  // Consent can change while the recorder bundle is loading, so re-evaluate.
  if (resolveConsentedRuntime(config, controller).recorder) {
    await startManagedRecorder(config);
  } else {
    const hadIdentity = managedRecorder?.getUserId?.() !== undefined;
    if (hadIdentity) {
      pendingUserId = undefined;
      pendingInternal = undefined;
      runtimeGeneration++;
    }
    cancelRetiredRecorders();
    await stopManagedRecorder(hadIdentity, 'consent-withdrawn');
  }

  if (resolveConsentedRuntime(config, controller).widget) {
    const widget = getWidgetRuntime();
    if (config.urlPolicy === 'origin-only' && widget.urlPolicyVersion !== 1) {
      throw new Error('[Perspectify Browser] Update the widget bundle for origin-only capture.');
    }
    widget.destroy?.();
    widget.init(buildWidgetConfig(config));
    managedWidgetRuntime = widget;
    if (config.urlPolicy === 'origin-only' && widget.getUrlPolicy?.() !== 'origin-only') {
      widget.destroy?.();
      managedWidgetRuntime = null;
      throw new Error('[Perspectify Browser] Widget URL policy was not applied.');
    }
  } else {
    window.PerspectifyWidget?.destroy?.();
  }
}

function cancelRetiredRecorders(): void {
  for (const recorder of retiredRecorders) {
    // stop(discard) cancels the uploader synchronously, including inactive instances.
    try { void recorder.stop?.(resolveRecorderStopMode('privacy-tightened')).catch(() => {}); }
    catch { /* A broken retired runtime must not prevent the remaining cancellations. */ }
  }
}

function assertManagedPrivacyCompatibility(): void {
  if (window.perspectifyRecorder && window.perspectifyRecorder !== managedRecorder) {
    throw new Error('[Perspectify Browser] Remove the unmanaged recorder before origin-only initialization.');
  }
  for (const recorder of [...retiredRecorders, ...(managedRecorder ? [managedRecorder] : [])]) {
    if (!privacyCapableRecorders.has(recorder)) throw new Error('[Perspectify Browser] Update the active recorder before origin-only initialization.');
  }
  const widget = window.PerspectifyWidget;
  if (widget && widget !== managedWidgetRuntime &&
      (widget.urlPolicyVersion !== 1 || widget.getUrlPolicy?.() !== undefined)) {
    throw new Error('[Perspectify Browser] Remove the unmanaged widget before origin-only initialization.');
  }
}

async function init(input: PerspectifyBrowserConfig): Promise<PerspectifyBrowserAPI> {
  const urlPolicy = normalizeUrlPolicy(input.urlPolicy);
  if (input.consentOnly !== true && !input.apiKey) {
    throw new Error('[Perspectify Browser] apiKey is required');
  }
  const config: PerspectifyBrowserConfig = { ...input, urlPolicy,
    recorder: input.recorder ? { ...input.recorder } : undefined,
    consent: input.consent ? { ...input.consent } : undefined,
    metadata: input.metadata ? { ...input.metadata } : undefined };
  if (urlPolicy === 'origin-only') assertManagedPrivacyCompatibility();
  const assetsBaseUrl = resolveAssetsBaseUrl(config);
  const compiledReleaseAssets = config.consentOnly === true ? null : resolveCompiledReleaseAssets(assetsBaseUrl);
  if (config.consentOnly !== true && !compiledReleaseAssets) resolveReleaseManifestUrl(config, assetsBaseUrl);
  // Order requests before the first await: an older manifest must not resurrect full capture.
  const requestGeneration = ++initRequestGeneration;
  if (urlPolicy === 'origin-only' || (config.consentOnly !== true && config.recorder?.enabled !== false)) {
    window.__perspectifyBrowserOwnsRecorder = true;
  }
  const tightening = urlPolicy === 'origin-only';
  if (tightening) cancelRetiredRecorders();
  let shutdown = tightening && (sdkInstance || activeConfig || activeConsentController || managedRecorder)
    ? teardown('privacy-tightened') : null;

  const releaseManifest = config.consentOnly === true || compiledReleaseAssets ? null : await resolveReleaseManifest(config);
  if (requestGeneration !== initRequestGeneration) return PerspectifyBrowser;
  // Full-mode configuration failures keep the current runtime alive, as before.
  if (!tightening && (sdkInstance || activeConfig || activeConsentController || managedRecorder)) shutdown = teardown('teardown');
  if (shutdown) await shutdown;
  if (requestGeneration !== initRequestGeneration) return PerspectifyBrowser;
  detectedAssetsBaseUrl = assetsBaseUrl;
  const nextRuntimeSignature = buildRuntimeSignature(config);
  activeConfig = config;
  const generation = ++runtimeGeneration;

  const releaseBootstrapOrigin = compiledReleaseAssets?.bootstrapOrigin ?? releaseManifest?.bootstrapOrigin;
  const localeCandidates = consentLocaleCandidates();
  const consentBootstrap = await fetchConsentPolicy(config, releaseBootstrapOrigin);
  if (generation !== runtimeGeneration || activeConfig !== config) return PerspectifyBrowser;
  if (!config.apiEndpoint && consentBootstrap.apiBaseUrl) {
    config.apiEndpoint = consentBootstrap.apiBaseUrl;
  }
  const consentPolicy = consentBootstrap.policy;
  if (config.consentOnly === true && !isValidRuntimeConsentPolicy(consentPolicy, config.consent?.projectId)) {
    throw new Error('[Perspectify Browser] Consent-only initialization requires an enabled, valid consent policy.');
  }
  if (consentPolicy) {
    activeConsentController = new ConsentController(
      config.consentOnly === true ? { ...consentPolicy, services: [] } : consentPolicy,
      consentBootstrap.bootstrapOrigin,
      window.location.origin,
      {
        urlPolicy,
        showManageButton: config.consent?.manageButtonVisible !== false,
        showBanner: config.consent?.bannerVisible !== false,
        autoConsent: config.consent?.autoConsent === true,
        styleNonce: config.consentStyleNonce,
        localeCandidates,
        feedbackTriggerPosition: floatingTriggerPosition(config),
      }
    );
    activeConsentController.init();
    activeConsentController.onChange(() => {
      void applyConsentControlledRuntime(config, activeConsentController);
    });
    startConsentLocaleWatch(config, activeConsentController, releaseBootstrapOrigin, localeCandidates);
  }

  attachConsentBridge(activeConsentController);
  await applyConsentControlledRuntime(config, activeConsentController);
  if (generation !== runtimeGeneration || activeConfig !== config) return PerspectifyBrowser;

  sdkInstance = PerspectifyBrowser;
  runtimeSignature = nextRuntimeSignature;
  return sdkInstance;
}

function open(): void {
  window.PerspectifyWidget?.open?.();
}

function close(): void {
  window.PerspectifyWidget?.close?.();
}

function sessionRecorder(): RecorderInstance | null | undefined {
  if (activeConfig?.urlPolicy !== 'origin-only') return window.perspectifyRecorder;
  // An unrelated global must never flush a full-URL recorder through this protected API.
  return managedRecorder?.getUrlPolicy?.() === 'origin-only' ? managedRecorder : null;
}

async function captureSession(): Promise<string | null> {
  const recorder = sessionRecorder();
  return recorder?.capture ? recorder.capture() : null;
}

function getSessionId(): string | null {
  return sessionRecorder()?.getSessionId?.() ?? null;
}

async function teardown(reason: RecorderStopReason): Promise<void> {
  pendingUserId = undefined;
  pendingInternal = undefined;
  activeConfig = null;
  runtimeGeneration++;
  window.PerspectifyWidget?.destroy?.();
  managedWidgetRuntime = null;
  // Consent timers and async widget submissions retire before any recorder upload await.
  stopConsentLocaleWatch?.();
  stopConsentLocaleWatch = null;
  activeConsentController?.destroy();
  activeConsentController = null;
  delete window.PerspectifyConsent;
  sdkInstance = null;
  runtimeSignature = null;
  if (reason === 'privacy-tightened') cancelRetiredRecorders();
  await stopManagedRecorder(managedRecorder?.getUserId?.() !== undefined, reason);
}

async function destroy(): Promise<void> {
  initRequestGeneration++;
  if (!sdkInstance && !activeConfig && !managedRecorder && !window.perspectifyRecorder && !activeConsentController) return;
  await teardown('teardown');
}

const PerspectifyBrowser: PerspectifyBrowserAPI = {
  init,
  open,
  close,
  captureSession,
  getSessionId,
  identify,
  resetIdentity,
  track,
  destroy,
};

function findAutoInitScript(): HTMLScriptElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (currentScript?.src?.includes('perspectify-browser')) {
    return currentScript;
  }

  return document.querySelector(
    'script[src*="perspectify-browser"][data-perspectify-api-key], script[src*="perspectify-browser"][data-api-key], script[src*="perspectify-browser"][data-perspectify-consent-only="true"]'
  ) as HTMLScriptElement | null;
}

function autoInit(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.PerspectifyBrowser = PerspectifyBrowser;

  const script = findAutoInitScript();
  const apiKey = script?.dataset.perspectifyApiKey || script?.dataset.apiKey;
  const consentOnly = parseBooleanDatasetValue(script?.dataset.perspectifyConsentOnly);
  if (!script || (!apiKey && consentOnly !== true)) {
    return;
  }

  const config: PerspectifyBrowserConfig = {
    apiKey,
    urlPolicy: script.dataset.perspectifyUrlPolicy as UrlPolicy | undefined,
    apiEndpoint: script.dataset.perspectifyApiEndpoint || script.dataset.apiEndpoint,
    assetsBaseUrl: new URL('.', script.src).toString(),
    releaseManifestUrl: script.dataset.perspectifyReleaseManifestUrl,
    bootstrapOrigin: script.dataset.perspectifyBootstrapOrigin,
    consentStyleNonce:
      script.dataset.perspectifyConsentStyleNonce ||
      script.nonce ||
      script.getAttribute('nonce') ||
      undefined,
    trigger: {
      type:
        parseBooleanDatasetValue(script.dataset.perspectifyTriggerVisible) === false
          ? 'none'
          : (script.dataset.perspectifyTriggerType as TriggerType | undefined) ?? 'floating',
      position: script.dataset.perspectifyTriggerPosition as TriggerPosition | undefined,
      label: script.dataset.perspectifyTriggerLabel || 'Feedback',
      keyboardShortcut: script.dataset.perspectifyKeyboardShortcut || 'ctrl+shift+f',
    },
    recorder: {
      enabled: script.dataset.perspectifyRecorder !== 'false',
      propagateTraceparent:
        parseBooleanDatasetValue(script.dataset.perspectifyPropagateTraceparent) ?? true,
      tracePropagationOrigins: parseCsvDatasetValue(script.dataset.perspectifyTraceOrigins),
    },
    consent: {
      enabled: script.dataset.perspectifyConsent !== 'false',
      projectId: script.dataset.perspectifyProjectId,
      manageButtonPosition:
        script.dataset.perspectifyConsentManagePosition as TriggerPosition | undefined,
      manageButtonVisible: parseBooleanDatasetValue(
        script.dataset.perspectifyConsentManageVisible
      ),
      bannerVisible: parseBooleanDatasetValue(
        script.dataset.perspectifyConsentBannerVisible
      ),
      autoConsent: parseBooleanDatasetValue(script.dataset.perspectifyConsentAuto),
    },
    consentOnly,
  };

  const generation = initRequestGeneration;
  const start = () => {
    if (generation !== initRequestGeneration) return;
    void init(config).catch((error) => {
      console.error('[Perspectify Browser] Failed to auto-initialize:', error);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}

autoInit();

export default PerspectifyBrowser;
